import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  OverrideListParams,
  CreateOverrideRequest,
  UpdateOverrideRequest,
} from "../types/overrides";
import * as overridesApi from "../services/overridesApi";
import { reviewKeys } from "./useReview";
import { validationKeys } from "./useValidation";

export const overrideKeys = {
  all: ["overrides"] as const,
  list: (params: OverrideListParams) =>
    [...overrideKeys.all, "list", params] as const,
};

export function useOverrides(params: OverrideListParams = {}) {
  return useQuery({
    queryKey: overrideKeys.list(params),
    queryFn: () => overridesApi.listOverrides(params),
  });
}

export function useCreateOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOverrideRequest) =>
      overridesApi.createOverride(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overrideKeys.all });
    },
  });
}

export function useUpdateOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateOverrideRequest }) =>
      overridesApi.updateOverride(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overrideKeys.all });
    },
  });
}

export function useDeleteOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => overridesApi.deleteOverride(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overrideKeys.all });
    },
  });
}

export function useApplyOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dryRun }: { id: number; dryRun: boolean }) =>
      overridesApi.applyOverride(id, dryRun),
    onSuccess: (_data, variables) => {
      if (variables.dryRun) return;
      // Issues count uses ["validation", "issues", "count", ...] which is
      // already matched by validationKeys.all (["validation"]).
      queryClient.invalidateQueries({ queryKey: overrideKeys.all });
      queryClient.invalidateQueries({ queryKey: validationKeys.all });
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}
